export const CS_ADMIN_ROLE_OPTIONS = [
  { value: 'IT_ADMIN', label: 'IT Admin' },
  { value: 'GROUP_SECRETARY', label: 'Group Company Secretary' },
  { value: 'COMPANY_SECRETARY', label: 'Company Secretary' },
];

/** Hidden from Users UI until external auditor access is enabled */
const CS_ADMIN_HIDDEN_USER_ROLES = new Set(['AUDITOR']);

/** Phase 1 users table only — company secretaries use portal accounts */
export const CS_ADMIN_USERS_MODULE_ROLES = CS_ADMIN_ROLE_OPTIONS.filter(
  (role) => role.value !== 'COMPANY_SECRETARY'
    && !CS_ADMIN_HIDDEN_USER_ROLES.has(role.value)
);

const ROLE_LABELS = {
  ...Object.fromEntries(CS_ADMIN_ROLE_OPTIONS.map((role) => [role.value, role.label])),
  AUDITOR: 'Auditor',
};

export function normalizeCsAdminRole(role) {
  const normalized = String(role || '').toUpperCase();
  switch (normalized) {
    case 'IT_ADMIN':
    case 'ADMIN':
      return 'IT_ADMIN';
    case 'SECRETARIAL_ADMIN':
    case 'GROUP_SECRETARY':
    case 'USER':
      return 'GROUP_SECRETARY';
    case 'SECRETARY':
    case 'COMPANY_SECRETARY':
      return 'COMPANY_SECRETARY';
    case 'AUDITOR':
      return 'AUDITOR';
    case 'VIEWER':
      return 'VIEWER';
    default:
      return normalized;
  }
}

export function getCsAdminRoleLabel(role) {
  return ROLE_LABELS[normalizeCsAdminRole(role)] || role || 'Unknown';
}

export function isGroupScopeRole(role) {
  return normalizeCsAdminRole(role) === 'GROUP_SECRETARY' || normalizeCsAdminRole(role) === 'IT_ADMIN';
}

export function isItAdminRole(role) {
  return normalizeCsAdminRole(role) === 'IT_ADMIN';
}

export function isSecretarialAdminRole(role) {
  return normalizeCsAdminRole(role) === 'GROUP_SECRETARY';
}

/** IT Admin only may change another user's global permission matrix on Users & Permissions. */
export function canManageUserPermissions(actorRole, targetRole) {
  if (!isItAdminRole(actorRole)) return false;
  const target = normalizeCsAdminRole(targetRole);
  return target === 'IT_ADMIN' || target === 'GROUP_SECRETARY';
}

/** IT Admin and Group Secretary may edit global permissions for portal accounts. */
export function canManagePortalAccountPermissions(actorRole, targetRole) {
  if (!isGroupScopeRole(actorRole)) return false;
  return normalizeCsAdminRole(targetRole) === 'COMPANY_SECRETARY';
}

/** Secretarial Admin and IT Admin may create and manage portal company secretary accounts. */
export function canManageCompanySecretaries(role) {
  return isGroupScopeRole(role);
}

export function isCompanySecretaryRole(role) {
  return normalizeCsAdminRole(role) === 'COMPANY_SECRETARY';
}

/** Group-scope admins can create parent or subsidiary companies */
export function canCreateParentCompany(role) {
  return isGroupScopeRole(role);
}

/** Company secretaries can create subsidiaries under assigned companies only */
export function canCreateSubsidiaryCompany(role) {
  return isGroupScopeRole(role) || isCompanySecretaryRole(role);
}

/** Show create / import company actions */
export function canCreateCompany(role) {
  return canCreateSubsidiaryCompany(role);
}

export function canAccessCompaniesList(role) {
  return isGroupScopeRole(role) || normalizeCsAdminRole(role) === 'COMPANY_SECRETARY';
}
